import { Link } from 'react-router-dom';
import { useBoolPreference, useNumberPreference } from '../lib/usePreference';
import {
  PITSTOP_RADIUS_PREF,
  PITSTOP_RADIUS_DEFAULT_KM,
  PITSTOP_RADIUS_MIN_KM,
  PITSTOP_RADIUS_MAX_KM,
} from '../lib/pitstop';
import { useThemePref, type ThemePref } from '../lib/theme';
import { VL_SIMULATE_PREF } from '../lib/vlMembership';
import {
  SIM_ENABLED_PREF,
  setSimLocation,
  clearSimLocation,
  restoreSimIfEnabled,
  loadSimCoords,
} from '../lib/simLocation';
import { geocodePlace, type GeocodeResult } from '../lib/poi';
import { useWakeLockStatus } from '../lib/useWakeLock';
import {
  useRideZoom,
  useExploreZoom,
  RIDE_ZOOM_MIN,
  RIDE_ZOOM_MAX,
  EXPLORE_ZOOM_MIN,
  EXPLORE_ZOOM_MAX,
} from '../lib/useRideSettings';
import {
  useActiveRegion,
  useIconSize,
  useSatelliteEnabled,
  useShowHillshade,
  useAdminMode,
  useAdminToken,
  useDiscoveryMode,
  useColorAssistMode,
  useRainModeEnabled,
  usePocketModeEnabled,
  useBatterySafeSize,
  BATTERY_SAFE_SIZES,
  BATTERY_SAFE_PRESETS,
  useRideNextAuraMeters,
  RIDE_NEXT_AURA_MIN,
  RIDE_NEXT_AURA_MAX,
  RIDE_NEXT_AURA_STEP,
  useElevationZoomLevels,
  ELEVATION_ZOOM_MIN,
  ELEVATION_ZOOM_MAX,
  isDynamicRegionSlug,
  type IconSize,
} from '../lib/useUiSettings';
import { REGIONS, REGION_IDS, isRegionId, type RegionId } from '@adtu/shared';
import { tilesManifest, type RegionManifestEntry } from '../lib/api';
import { useEffect, useState } from 'react';
import {
  CRUX_QUEUE_CHANGED_EVENT,
  flushCruxQueue,
  listPendingCruxItems,
  type PendingCruxItem,
} from '../lib/admin';
import { useOnline } from '../lib/useOnline';
import { fetchMe, logout, roleAtLeast, type SessionUser } from '../lib/auth';
import {
  getPushState,
  isPushSupported,
  subscribe as pushSubscribe,
  unsubscribe as pushUnsubscribe,
  type PushSubscribedState,
} from '../lib/push';

// Wichtig: dieser Key MUSS mit dem in App.tsx matchen, sonst liest der
// Root-Hook den falschen Wert und der Toggle wirkt nicht.
export const PREF_SCREEN_AWAKE = 'adt_screen_awake';

/**
 * Ein wählbarer Eintrag im Region-Switcher. Quelle sind zwei Töpfe:
 *   - die fest gebauten Regionen aus shared/REGION_IDS (immer da, auch offline)
 *   - dynamische Self-Service-Korridore aus dem Server-Manifest
 * `dynamic` unterscheidet die beiden, damit die UI beim Korridor einen
 * anderen Hinweistext zeigen kann (keine Route/Umriss-Overlays, 72-h-Ablauf).
 */
interface RegionChoice {
  id: string;
  humanName: string;
  dynamic: boolean;
}

// Wave 14ed: REGION_IDS ist leer, solange keine statische Region
// einkompiliert ist — liefert also normalerweise []. Die Funktion bleibt,
// damit ein spaeterer statischer Eintrag ohne UI-Aenderung wieder greift.
function staticRegionChoices(): RegionChoice[] {
  return REGION_IDS.flatMap((id) => {
    const info = REGIONS[id];
    return info ? [{ id, humanName: info.humanName, dynamic: false }] : [];
  });
}

export function ProfilePage() {
  const [screenAwake, setScreenAwake] = useBoolPreference(PREF_SCREEN_AWAKE, true);
  // Live-Status vom Singleton-Manager. Gibt uns active/supported/pending — so
  // sieht der User sofort ob der Lock tatsächlich greift (auf iOS greift er
  // u. U. erst nach dem ersten Tap irgendwohin in der App).
  const { active, supported, pending } = useWakeLockStatus();

  const [rideZoom, setRideZoom] = useRideZoom();
  const [exploreZoom, setExploreZoom] = useExploreZoom();
  const [iconSize, setIconSize] = useIconSize();
  const [satelliteEnabled, setSatelliteEnabled] = useSatelliteEnabled();
  const [showHillshade, setShowHillshade] = useShowHillshade();
  const [adminMode, setAdminMode] = useAdminMode();
  const [adminToken, setAdminToken] = useAdminToken();
  const [discoveryMode, setDiscoveryMode] = useDiscoveryMode();
  // Wave 14ca-fix64: Color-Assist-Modus (Rot-Gruen-Schwaeche). Admin-Toggle.
  const [colorAssistMode, setColorAssistMode] = useColorAssistMode();
  // Wave 14ca-fix64: Rain-Mode-Feature-Gate. Admin-Toggle. Wenn OFF, ist der
  // 🌧️-Button im Map-View versteckt — fuer normale User die das Feature
  // (noch) nicht sehen sollen.
  const [rainModeEnabled, setRainModeEnabled] = useRainModeEnabled();
  // Wave 14ca-fix64: Pocket-Mode-Feature-Gate. Analog zu Rain-Mode.
  const [pocketModeEnabled, setPocketModeEnabled] = usePocketModeEnabled();
  const [batterySafeSize, setBatterySafeSize] = useBatterySafeSize();
  // Wave 14cf: Active-Region — User kann zwischen Liguria und Elba wechseln
  // ohne durch Onboarding-Reset gehen zu müssen.
  const [activeRegion, setActiveRegion] = useActiveRegion();
  // Corridor-Selfservice: der Switcher listete bisher nur REGION_IDS, also
  // ausschließlich die fest gebauten Regionen. Ein selbst gebauter Korridor
  // tauchte damit auf der Offline-Seite auf (die liest das Manifest), hier
  // aber nicht — der User konnte ihn im Profil nicht aktivieren und sah ihn
  // folglich nie auf der Karte. Jetzt ist auch dieser Switcher manifest-
  // getrieben; die statische Liste bleibt als Offline-Fallback.
  const [regionChoices, setRegionChoices] = useState<RegionChoice[]>(staticRegionChoices);
  // Wave 14cf: Ride-Next-Aura — User stellt ein wie viele Meter die orange
  // Vorwärts-Aura bei der Route-Verfolgung im Ride-Mode hervorhebt.
  const [rideNextAuraMeters, setRideNextAuraMeters] = useRideNextAuraMeters();
  // Wave 14ew: zwei Höhenprofil-Zoom-Stufen (Doppelklick im Profil cycelt
  // full → near → far). Setter clampt + erzwingt near < far.
  const [[elevZoomNear, elevZoomFar], setElevZoomLevels] =
    useElevationZoomLevels();
  const [showToken, setShowToken] = useState(false);
  // Wave 14cm: Simulate-Vertical-Lodge-Toggle. Nur sichtbar für
  // Voll-Admins. Wirkt sich auf useVlMembership-Hook in Map/Navi aus.
  const [vlSimulate, setVlSimulate] = useBoolPreference(VL_SIMULATE_PREF, false);
  // ULTRA: Test-Standort-Simulation. An/aus persistent; gesetzt wird der Punkt
  // per Long-Press auf die Karte. Beim Abschalten echtes GPS wiederherstellen.
  const [pitstopRadiusKm, setPitstopRadiusKm] = useNumberPreference(
    PITSTOP_RADIUS_PREF,
    PITSTOP_RADIUS_DEFAULT_KM,
  );
  const [simEnabled, setSimEnabled] = useBoolPreference(SIM_ENABLED_PREF, false);
  useEffect(() => {
    if (simEnabled) restoreSimIfEnabled();
    else clearSimLocation();
  }, [simEnabled]);

  // Manifest holen und die Korridor-Regionen hinten anhängen. forceNetwork,
  // damit ein frisch gebauter Korridor sofort erscheint statt erst nach
  // Ablauf des Manifest-Caches. Schlägt der Call fehl (offline, Server weg),
  // bleibt die statische Liste stehen — der Switcher ist nie leer.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const manifest = await tilesManifest({ forceNetwork: true });
        if (aborted) return;
        const entries: RegionManifestEntry[] = manifest.regions ?? [];
        const merged = staticRegionChoices();
        for (const entry of entries) {
          if (merged.some((m) => m.id === entry.id)) continue;
          // Nur was als Region-ID oder als Korridor-Slug durchgeht — ein
          // kaputter Manifest-Eintrag soll keinen toten Chip erzeugen.
          if (!isRegionId(entry.id) && !isDynamicRegionSlug(entry.id)) continue;
          merged.push({
            id: entry.id,
            humanName: entry.humanName || entry.id,
            dynamic: !isRegionId(entry.id),
          });
        }
        setRegionChoices(merged);
      } catch {
        // offline → staticRegionChoices bleibt aktiv
      }
    })();
    return () => {
      aborted = true;
    };
  }, []);

  // Ein Korridor, der aktiv ist aber nicht (mehr) im Manifest steht — z. B.
  // nach dem 72-h-Ablauf oder wenn das Manifest gerade nicht erreichbar ist.
  // Ohne diesen Zusatz stünde der Switcher auf "nichts markiert" und der
  // User könnte nicht sehen, was eigentlich eingestellt ist.
  // Wave 14ed: `activeRegion` kann '' sein (frischer Account, noch kein
  // Korridor gebaut). Dann darf hier KEIN leerer Chip entstehen.
  const visibleRegionChoices: RegionChoice[] =
    !activeRegion || regionChoices.some((c) => c.id === activeRegion)
      ? regionChoices
      : [...regionChoices, { id: activeRegion, humanName: activeRegion, dynamic: true }];
  const activeIsDynamic = visibleRegionChoices.some(
    (c) => c.id === activeRegion && c.dynamic,
  );
  // Ortssuche für den Test-Standort (Geocode via Backend/Google).
  const [simQuery, setSimQuery] = useState('');
  const [simResults, setSimResults] = useState<GeocodeResult[] | null>(null);
  const [simSearching, setSimSearching] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);
  const [simPlace, setSimPlace] = useState<string | null>(null);
  const doSimSearch = async () => {
    const q = simQuery.trim();
    if (!q) return;
    setSimSearching(true);
    setSimError(null);
    setSimResults(null);
    try {
      const r = await geocodePlace(q);
      setSimResults(r);
      if (r.length === 0) setSimError('Kein Ort gefunden.');
    } catch (e) {
      setSimError(e instanceof Error ? e.message : String(e));
    } finally {
      setSimSearching(false);
    }
  };
  const pickSimPlace = (r: GeocodeResult) => {
    setSimLocation(r.lat, r.lon);
    setSimPlace(r.name);
    setSimResults(null);
    setSimQuery('');
  };
  // Wave 14de: 3-Wege-Theme-Picker. Default 'light' damit die App in der
  // Sonne lesbar ist (User-Wunsch).
  const { pref: themePref, setPref: setThemePref } = useThemePref();

  // Wave 14ca-fix31: Admin-Section gated by user.role. Nur 'admin' sehen
  // den Admin-Block (mit direktem /admin/-Link, ohne Token-Eingabe weil
  // Cookie-Auth reicht). 'admin_light' bekommt nur on-device admin
  // (Mark-Crux-Button im Trail-Sheet) ohne SPA-Link. 'user' sieht nichts.
  const [role, setRole] = useState<SessionUser['role'] | null>(null);
  useEffect(() => {
    fetchMe().then((u) => setRole(u?.role ?? null)).catch(() => setRole(null));
  }, []);

  return (
    <div
      className="absolute inset-0 overflow-y-auto px-4 pb-4 space-y-6"
      // Wave 14dh: Top-Padding = safe-area + 1rem damit die Open-admin-Card
      // nicht unter der Dynamic Island sitzt (vorher nur p-4 = 16px ohne
      // safe-area). Auf Geräten ohne Notch fällt die safe-area auf 0.
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
    >
      {/* Wave 14cp: Quick "Open admin" Card ganz oben.
          User-Bug: "ich muss da so oft rein, das sollte als erstes oben
          sein, sonst nervt das scrollen". Sichtbar für admin und
          admin_light_plus; admin_light hat keinen Admin-SPA-Zugang. */}
      {roleAtLeast(role, 'admin_light_plus') && (
        <a
          href="/admin/"
          className="block bg-turbo-700/30 border border-amber-500/40 rounded-xl p-4 no-underline active:bg-turbo-700/45 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span aria-hidden className="text-2xl">🛠️</span>
            <div className="flex-1">
              <div className="font-medium text-white">Open admin</div>
              <div className="text-xs text-white/55 mt-0.5">
                {role === 'admin'
                  ? 'Manage trails, runs, users, battery stations.'
                  : 'Manage trails and routes.'}
              </div>
            </div>
            <span aria-hidden className="text-white/40 text-lg">→</span>
          </div>
        </a>
      )}

      <section>
        <h1 className="text-xl font-semibold mb-3">Settings</h1>

        {/* Wave 14ce: Offline-Downloads als Profile-Tile (Tab wurde durch
            SOS ersetzt). OfflinePage selbst ist unverändert — wir linken
            nur darauf. Erste Sektion damit es nicht im Settings-Fluss
            untergeht. */}
        <Link
          to="/offline"
          className="block bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4 mb-3 no-underline active:bg-turbo-700/45 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span aria-hidden className="text-2xl">📥</span>
            <div className="flex-1">
              <div className="font-medium text-white">Offline downloads</div>
              <div className="text-xs text-white/55 mt-0.5">
                Manage maps + POI data per region for trails without signal.
              </div>
            </div>
            <span aria-hidden className="text-white/40 text-lg">→</span>
          </div>
        </Link>

        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={screenAwake}
              onChange={(e) => setScreenAwake(e.target.checked)}
              className="mt-1 w-5 h-5 accent-turbo-500 shrink-0"
              aria-label="Keep screen awake while riding"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">Keep screen awake</span>
                {screenAwake && <WakeLockBadge active={active} supported={supported} pending={pending} />}
              </div>
              {/* Wave 14ca-fix29: Beschreibungstext entfernt — selbsterklärend. */}
              {screenAwake && pending && (
                <div className="text-xs text-amber-300 mt-2 leading-relaxed">
                  ℹ️ iOS needs a tap to activate — tap anywhere in the app.
                </div>
              )}
              {!supported && (
                <div className="text-xs text-amber-300 mt-2 leading-relaxed">
                  ⚠️ Needs iOS 16.4+ — toggle has no effect on older versions.
                </div>
              )}
            </div>
          </label>
        </div>
      </section>

      {/* Wave 14cf: Region-Switcher.
          User wählt zwischen Liguria und Elba. Wirkt sich aus auf:
            - Initial-Camera der Map (Bounds + Center)
            - Routing-DB (navi-{region}.sqlite)
            - Trail-Liste (gefiltert nach Region)
          Offline-Cache bleibt unabhängig — beide Regionen können parallel
          offline gehalten werden, der Switcher tauscht nur die "aktive"
          Default-Sicht. */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Region</h2>
        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">
            Active region
          </div>
          {/* Wave 14es: flex-wrap + min-width statt flex-1 fuer die Chips,
              damit ab 4-5 Regionen die hinteren in eine zweite Reihe rutschen
              statt off-screen zu verschwinden. "Pyrenees Coast" hat zwei
              Worte → flex-1 squeezed alle Chips so eng dass das Wort
              umbricht; mit min-w gibt jedes Chip seine echte Breite + wrap. */}
          {visibleRegionChoices.length === 0 && (
            <p className="text-[13px] text-white/60 leading-relaxed">
              No region yet — nothing is downloaded and nothing is preset.
              Build your own corridor under Navi &middot; Corridor from a GPX
              and it shows up here.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {visibleRegionChoices.map((choice) => {
              const isActive = choice.id === activeRegion;
              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => setActiveRegion(choice.id as RegionId)}
                  className={`grow basis-[calc(33%-0.5rem)] min-w-[5.5rem] rounded-lg py-2.5 px-2 text-sm font-medium border transition ${
                    isActive
                      ? 'bg-turbo-500 border-turbo-500 text-white'
                      : 'bg-turbo-900/40 border-turbo-500/30 text-white/80 hover:bg-turbo-900/60'
                  }`}
                >
                  {choice.humanName}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-white/45 mt-2 leading-relaxed">
            Switches the map's initial view, routing engine and trail list.
            Offline downloads are managed per region in the Offline downloads
            tile above. Corridors you built yourself show up here as soon as
            the build is done.
          </p>
          {activeIsDynamic && (
            <p className="text-[11px] text-white/45 mt-1 leading-relaxed">
              A self-built corridor is active — map, POIs and routing come from
              that corridor package. It stays on the server for 72 h; the
              downloaded copy on this phone is yours to keep.
            </p>
          )}
        </div>
      </section>

      {/* Wave 14cf: Ride-Next-Aura — orange Highlight der nächsten N Meter
          entlang der Route, gegen Disorientation auf komplexen Loops. */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Riding</h2>
        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4">
          <label className="block">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">Lightsaber</span>
              <span className="text-sm text-white/70 tabular-nums">
                {rideNextAuraMeters === 0 ? 'off' : `${rideNextAuraMeters} m`}
              </span>
            </div>
            <input
              type="range"
              min={RIDE_NEXT_AURA_MIN}
              max={RIDE_NEXT_AURA_MAX}
              step={RIDE_NEXT_AURA_STEP}
              value={rideNextAuraMeters}
              onChange={(e) => setRideNextAuraMeters(Number(e.target.value))}
              className="w-full mt-2 accent-orange-500"
              aria-label="Length of forward-route aura while riding"
            />
            <div className="flex justify-between text-[10px] text-white/40 -mt-1">
              <span>off</span>
              <span>{RIDE_NEXT_AURA_MAX} m</span>
            </div>
            <div className="text-xs text-white/60 mt-1 leading-relaxed">
              Highlights the next {rideNextAuraMeters || '500'} m of your route
              with an orange glow during a ride — useful on loops or trail
              clusters where it's not obvious which branch belongs to your tour.
              Set to <em>off</em> to disable.
            </div>
          </label>
        </div>
      </section>

      {/* Wave 14ew: Höhenprofil-Zoom-Stufen. Doppelklick im Profil cycelt
          full → near → far. Beide Werte hier einstellbar (Default 1000/5000).
          Live-Save; der Setter clampt in [MIN,MAX] und hält near < far. */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Elevation profile</h2>
        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4 space-y-4">
          <label className="block">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">Zoom level 1 (near)</span>
              <span className="text-sm text-white/70 tabular-nums">
                {elevZoomNear} m
              </span>
            </div>
            <input
              type="range"
              min={ELEVATION_ZOOM_MIN}
              max={ELEVATION_ZOOM_MAX}
              step={100}
              value={elevZoomNear}
              onChange={(e) =>
                setElevZoomLevels([Number(e.target.value), elevZoomFar])
              }
              className="w-full mt-2 accent-orange-500"
              aria-label="Elevation profile near zoom window in meters"
            />
          </label>
          <label className="block">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium">Zoom level 2 (far)</span>
              <span className="text-sm text-white/70 tabular-nums">
                {elevZoomFar} m
              </span>
            </div>
            <input
              type="range"
              min={ELEVATION_ZOOM_MIN}
              max={ELEVATION_ZOOM_MAX}
              step={100}
              value={elevZoomFar}
              onChange={(e) =>
                setElevZoomLevels([elevZoomNear, Number(e.target.value)])
              }
              className="w-full mt-2 accent-orange-500"
              aria-label="Elevation profile far zoom window in meters"
            />
          </label>
          <div className="text-xs text-white/60 leading-relaxed">
            Double-tap the elevation profile to cycle through the full view,
            then these two zoom windows. The near value is automatically kept
            below the far value.
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Controls</h2>

        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4 space-y-5">
          {/* Wave 14dx: Glove-Mode reaktiviert. Skaliert NUR die Map-Controls
              oben rechts (Locate + Compass) und unten rechts (Zoom +/-,
              Fullscreen) um ~40% groesser. Style-Switcher oben links bleibt
              normal. Pills werden parallel etwas transparenter damit die
              groesseren Buttons die Karte nicht zudecken. */}
          <div>
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <span className="font-medium">Icon size</span>
              <span className="text-sm text-white/55 capitalize">{iconSize}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(['normal', 'gloves'] as const).map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setIconSize(size as IconSize)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                    iconSize === size
                      ? 'bg-turbo-500/60 text-white'
                      : 'bg-turbo-900/40 text-white/70 hover:bg-turbo-900/60'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
            <div className="text-xs text-white/55 mt-2 leading-relaxed">
              Glove mode enlarges the zoom, fullscreen and pocket-mode
              buttons (~35%) for easier tapping with gloves. Locate +
              compass stay normal-sized so they don't collide.
            </div>
          </div>

          {/* Satellite-Toggle — default OFF. User-Wunsch: "default weg,
              verwirrt, braucht man fast nie. mach sie optional im profile." */}
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={satelliteEnabled}
              onChange={(e) => setSatelliteEnabled(e.target.checked)}
              className="mt-1 w-5 h-5 accent-turbo-500 shrink-0"
              aria-label="Enable satellite map style"
            />
            <div className="flex-1">
              <div className="font-medium">Enable satellite style</div>
              {/* Wave 14ca-fix29: Beschreibung entfernt — selbsterklärend. */}
            </div>
          </label>

          {/* Hillshade-Toggle — Wave 14bp Akku-Optimierung. Default AN, damit
              sich für existing User visuell nichts ändert. */}
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showHillshade}
              onChange={(e) => setShowHillshade(e.target.checked)}
              className="mt-1 w-5 h-5 accent-turbo-500 shrink-0"
              aria-label="Show hillshade terrain shading"
            />
            <div className="flex-1">
              <div className="font-medium">Show hillshade</div>
              <div className="text-sm text-white/60 mt-1">
                Auto-off in compass heads-up mode to save battery.
              </div>
            </div>
          </label>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Map modes</h2>

        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4 space-y-5">
          {/* Ride-Zoom — beim Drücken von 'Ride' im Trail-Sheet fliegt die
              Karte auf diesen Zoom-Level. Auto-Follow ist immer an im Ride-Mode. */}
          <ZoomSlider
            label="Ride zoom"
            hint="When you tap »Start ride« the map jumps to this zoom level. Auto-follow is always on."
            min={RIDE_ZOOM_MIN}
            max={RIDE_ZOOM_MAX}
            value={rideZoom}
            onChange={setRideZoom}
          />

          {/* Explore-Zoom — default für Locate-Taps im Explore-Mode. */}
          <ZoomSlider
            label="Explore zoom"
            hint="Default zoom on app start and when tapping Locate in Explore mode."
            min={EXPLORE_ZOOM_MIN}
            max={EXPLORE_ZOOM_MAX}
            value={exploreZoom}
            onChange={setExploreZoom}
          />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2">Profile</h2>
        <p className="text-white/60 text-sm">
          Coming in Phase 2: login via email invite, display of unlocked swap
          days, individual battery-usage factor (after a test uphill).
        </p>
      </section>

      {/* Wave 14ca-fix64: Display & Accessibility — fuer ALLE User.
          User-Wunsch: "drei Features sollten fuer jeden User da sein und
          nicht nur fuer den Admin und schon mal gar nicht nur wenn man
          den Admin-Mode aktiviert hat". Daher hier ausgegliedert aus dem
          Admin-Block: Color-Assist (Rot-Gruen-Schwaeche),
          Rain-Mode (Display-Lock gegen Tropfen) und Pocket-Mode (OLED-
          Energie-Spar) — sind alle Accessibility/UX-Features die jeder
          braucht der so unterwegs ist. */}
      <section>
        <h2 className="text-lg font-semibold mb-3">
          Display & accessibility
        </h2>
        <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4 space-y-4">
          {/* Wave 14de: Theme-Picker. Default 'light' — Dark-Mode war in
              der Sonne schlecht lesbar. Segmented-Control mit 3 Optionen. */}
          <div>
            <div className="font-medium mb-1.5">Appearance</div>
            <div className="grid grid-cols-3 gap-1.5 p-1 rounded-xl bg-turbo-800/60 border border-turbo-700/50">
              {(['light', 'dark', 'system'] as ThemePref[]).map((opt) => {
                const isActive = themePref === opt;
                const label =
                  opt === 'light'
                    ? 'Light'
                    : opt === 'dark'
                      ? 'Dark'
                      : 'System';
                const icon =
                  opt === 'light' ? '☀️' : opt === 'dark' ? '🌙' : '📱';
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setThemePref(opt)}
                    aria-pressed={isActive}
                    className={`rounded-lg py-2 px-2 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                      isActive
                        ? 'bg-sky-500/30 border border-sky-400/60 text-white shadow-sm'
                        : 'text-white/65 hover:text-white/85 active:bg-turbo-700/40'
                    }`}
                  >
                    <span aria-hidden>{icon}</span>
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-white/55 mt-1.5 leading-snug">
              Light is easier to read in bright sunlight. System follows
              your phone's setting.
            </p>
          </div>

          {/* Color-Assist (Rot-Grün-Schwäche) */}
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={colorAssistMode}
              onChange={(e) => setColorAssistMode(e.target.checked)}
              className="mt-1 w-5 h-5 accent-turbo-500 shrink-0"
              aria-label="Enable color-assist mode for red-green deficiency"
            />
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2 flex-wrap">
                <span>Color mode: Red-green deficiency</span>
                {colorAssistMode && (
                  <span className="text-[10px] uppercase tracking-wider bg-cyan-500/30 text-cyan-200 px-1.5 py-0.5 rounded">
                    active
                  </span>
                )}
              </div>
              <div className="text-sm text-white/60 mt-1 leading-relaxed">
                Colorblind-friendly trail palette based on Wong (Nature
                Methods, 2011). <em>Easy</em> switches from green to{' '}
                <span style={{ color: '#26C6DA' }}>cyan</span>,{' '}
                <em>Hard</em> and <em>Intermediate +</em> from red to{' '}
                <span style={{ color: '#D55E00' }}>vermillion</span>.
                Applies everywhere: map lines, trail filter swatches and
                the trail edit sheet.
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                {(
                  [
                    'uphill',
                    'easy',
                    'intermediate',
                    'intermediate_plus',
                    'hard',
                    'expert',
                    'extreme',
                  ] as const
                ).map((d) => (
                  <span
                    key={d}
                    className="inline-block w-5 h-5 rounded border border-white/25"
                    style={{
                      background:
                        d === 'extreme'
                          ? 'repeating-linear-gradient(45deg, #111 0 3px, #fff 3px 6px)'
                          : d === 'intermediate_plus'
                            ? colorAssistMode
                              ? 'repeating-linear-gradient(45deg, #1565c0 0 3px, #D55E00 3px 6px)'
                              : 'repeating-linear-gradient(45deg, #1565c0 0 3px, #d32f2f 3px 6px)'
                            : d === 'easy'
                              ? colorAssistMode
                                ? '#26C6DA'
                                : '#76bb40'
                              : d === 'hard'
                                ? colorAssistMode
                                  ? '#D55E00'
                                  : '#d32f2f'
                                : d === 'intermediate'
                                  ? '#1565c0'
                                  : d === 'expert'
                                    ? '#111111'
                                    : '#ec4899',
                    }}
                    aria-hidden="true"
                    title={d}
                  />
                ))}
              </div>
            </div>
          </label>

          {/* Rain-Mode */}
          <label className="flex items-start gap-3 cursor-pointer select-none pt-4 border-t border-white/10">
            <input
              type="checkbox"
              checked={rainModeEnabled}
              onChange={(e) => setRainModeEnabled(e.target.checked)}
              className="mt-1 w-5 h-5 accent-turbo-500 shrink-0"
              aria-label="Enable rain-mode feature"
            />
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2 flex-wrap">
                <span>Rain mode (display lock)</span>
                {rainModeEnabled && (
                  <span className="text-[10px] uppercase tracking-wider bg-sky-500/30 text-sky-200 px-1.5 py-0.5 rounded">
                    active
                  </span>
                )}
              </div>
              <div className="text-sm text-white/60 mt-1 leading-relaxed">
                Adds a <span aria-hidden="true">🌧️</span> button to the
                map that locks the touchscreen during rain. Without it,
                raindrops on the screen register as taps (a common
                bike/motorcycle issue). To unlock — and prevent accidental
                taps from doing so — <strong>hold 3 fingers for 2 s</strong>.
                Recording keeps running while locked.
              </div>
            </div>
          </label>

          {/* Pocket-Mode */}
          <label className="flex items-start gap-3 cursor-pointer select-none pt-4 border-t border-white/10">
            <input
              type="checkbox"
              checked={pocketModeEnabled}
              onChange={(e) => setPocketModeEnabled(e.target.checked)}
              className="mt-1 w-5 h-5 accent-turbo-500 shrink-0"
              aria-label="Enable battery safe mode"
            />
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2 flex-wrap">
                <span>Battery safe mode</span>
                {pocketModeEnabled && (
                  <span className="text-[10px] uppercase tracking-wider bg-emerald-500/30 text-emerald-200 px-1.5 py-0.5 rounded">
                    active
                  </span>
                )}
              </div>
              <div className="text-sm text-white/60 mt-1 leading-relaxed">
                Adds a <span aria-hidden="true">🔋</span> button to the
                map. Tap to dim almost the whole screen to black, leaving a
                small stamp-sized map up top with your position and route.
                On OLED iPhones (X+) black pixels are physically off —
                roughly 50–60 % less display power than a normal map view.
                Recording
                keeps running (the wake lock keeps JS active). Tip: drop
                your screen brightness to the minimum first — iOS won't
                let the app change brightness, but the few non-black
                pixels (the status pill) will benefit. To unlock:{' '}
                <strong>hold 3 fingers for 2 s</strong> (same gesture as
                rain mode).
              </div>

              {/* Wave 14dt: Briefmarken-Größe als 5-Stufen-Slider. Nur sichtbar
                  wenn der Mode aktiviert ist — kleiner = mehr Schwarzfläche =
                  mehr Battery-Save, größer = besser lesbar. */}
              {pocketModeEnabled && (
                <div className="mt-3">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-xs text-white/50">Window size</span>
                    <span className="text-xs text-white/70 tabular-nums">
                      {BATTERY_SAFE_PRESETS[batterySafeSize].label} ·{' '}
                      {BATTERY_SAFE_PRESETS[batterySafeSize].w}×
                      {BATTERY_SAFE_PRESETS[batterySafeSize].h}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={BATTERY_SAFE_SIZES.length - 1}
                    step={1}
                    value={BATTERY_SAFE_SIZES.indexOf(batterySafeSize)}
                    onChange={(e) =>
                      setBatterySafeSize(
                        BATTERY_SAFE_SIZES[Number(e.target.value)] ??
                          batterySafeSize,
                      )
                    }
                    className="w-full accent-turbo-500"
                    aria-label="Battery safe mode window size"
                  />
                </div>
              )}
            </div>
          </label>
        </div>
      </section>

      {/* Wave 14ca-fix31 + Wave 14cb: Admin-Section gated by role-Hierarchie.
          - 'admin'            → /admin/-Link + on-device admin + Discovery
          - 'admin_light_plus' → /admin/-Link (kann Trails+Routen mgmt'n) +
                                 on-device admin + Discovery
          - 'admin_light'      → nur on-device admin (Crux/Gefahrenstellen +
                                 Description). KEIN Discovery, KEIN /admin/.
          - 'user'             → komplett unsichtbar (auch Section-Header).
          - role===null        → erste Render-Phase, fetchMe noch im Flug →
                                 unsichtbar (kein Flicker für normale User). */}
      {roleAtLeast(role, 'admin_light') && (
        <section>
          <h2 className="text-lg font-semibold mb-3">
            {role === 'admin'
              ? 'Admin'
              : role === 'admin_light_plus'
                ? 'Admin (light+)'
                : 'Admin (light)'}
          </h2>
          <div className="bg-turbo-700/30 border border-turbo-500/20 rounded-xl p-4 space-y-5">
            {/* Voll-Admin und Light+ duerfen ins Admin-SPA — Light+ kann
                Trails + Routen managen, Voll-Admin alles inkl. Users. */}
            {roleAtLeast(role, 'admin_light_plus') && (
              <a
                href="/admin/"
                className="flex items-center justify-between gap-3 text-white hover:text-turbo-300 transition-colors"
              >
                <div>
                  <div className="font-medium">Open admin</div>
                  <div className="text-sm text-white/60 mt-0.5">
                    {role === 'admin'
                      ? 'Manage trails, runs, users.'
                      : 'Manage trails and routes.'}
                  </div>
                </div>
                <span className="text-white/40 text-lg">→</span>
              </a>
            )}

            {/* ULTRA: Position simulieren — sichtbar für alle Admin-Rollen.
                Aktivieren, dann auf der Karte LANG drücken → Bestätigungs-Popup,
                oder unten einen Ort suchen. Solange aktiv gilt der Punkt als
                dein Standort; Fahrtrichtung kommt bei aktivem GPX-Ride aus der
                Route. (On-device admin mode + VL-Simulate wurden für ULTRA
                entfernt.) */}
            <div className="border-t border-white/10 -mx-4 px-4 pt-4">
              <div>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={simEnabled}
                      onChange={(e) => setSimEnabled(e.target.checked)}
                      className="mt-1 w-5 h-5 accent-fuchsia-500 shrink-0"
                      aria-label="Andere Position simulieren"
                    />
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        <span>🧪 Andere Position simulieren</span>
                        {simEnabled && (
                          <span className="text-[10px] uppercase tracking-wider bg-fuchsia-500/30 text-fuchsia-200 px-1.5 py-0.5 rounded">
                            on
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-white/60 mt-1">
                        Testet Routing/Pit-Stop, als wärst du woanders.
                        Aktivieren, dann auf der <strong>Karte lang drücken</strong>{' '}
                        (~0,6s) → „Als Test-Standort speichern?" bestätigen.
                        Alternativ unten einen Ort suchen. Die Fahrtrichtung
                        kommt bei aktivem GPX-Ride automatisch aus der Route.
                        Abschalten → zurück zum echten GPS.
                      </div>
                    </div>
                  </label>

                  {simEnabled && (
                    <div className="mt-3 ml-8">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={simQuery}
                          onChange={(e) => setSimQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void doSimSearch();
                          }}
                          placeholder="Ort suchen (z.B. Schladming)"
                          className="flex-1 rounded-lg bg-turbo-800/70 border border-turbo-700/60 px-3 py-2 text-sm text-white placeholder:text-white/40"
                        />
                        <button
                          type="button"
                          onClick={() => void doSimSearch()}
                          disabled={simSearching || !simQuery.trim()}
                          className="rounded-lg bg-fuchsia-600/30 border border-fuchsia-400/50 text-fuchsia-50 text-sm px-3 disabled:opacity-50"
                        >
                          {simSearching ? '…' : 'Suchen'}
                        </button>
                      </div>

                      {simError && (
                        <div className="text-amber-300 text-[13px] mt-2">{simError}</div>
                      )}

                      {simResults && simResults.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {simResults.map((r) => (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => pickSimPlace(r)}
                              className="w-full text-left rounded-lg border border-white/10 bg-turbo-700/40 hover:bg-turbo-700/60 p-2"
                            >
                              <div className="text-sm text-white font-medium">{r.name}</div>
                              {r.address && (
                                <div className="text-[12px] text-white/50 truncate">{r.address}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="text-[12px] text-fuchsia-200/80 mt-2">
                        {simPlace
                          ? `🧪 Aktiver Test-Standort: ${simPlace}`
                          : loadSimCoords()
                            ? `🧪 Aktiver Test-Standort: ${loadSimCoords()!.lat.toFixed(4)}, ${loadSimCoords()!.lon.toFixed(4)}`
                            : 'Noch kein Ort gewählt — oben suchen.'}
                      </div>
                    </div>
                  )}
              </div>

              {/* ULTRA: Such-Radius für "Nächster Stopp voraus". Kleiner =
                  weniger Kandidaten + kürzere Umweg-Routings = schneller.
                  Der Pit-Stop sucht nur in unmittelbarer Nähe. */}
              <div className="border-t border-white/10 -mx-4 px-4 pt-4 mt-4">
                <div className="font-medium">🍽️ Pit-Stop-Radius</div>
                <div className="text-sm text-white/60 mt-1 mb-2">
                  "Nächster Stopp voraus" sucht nur innerhalb dieses Radius.
                  Kleiner = schneller (weniger + kürzere Umweg-Berechnungen).
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={PITSTOP_RADIUS_MIN_KM}
                    max={PITSTOP_RADIUS_MAX_KM}
                    step={5}
                    value={pitstopRadiusKm}
                    onChange={(e) => setPitstopRadiusKm(Number(e.target.value))}
                    className="flex-1 accent-fuchsia-500"
                    aria-label="Pit-Stop-Radius in Kilometern"
                  />
                  <span className="text-sm tabular-nums w-14 text-right">
                    {Math.round(pitstopRadiusKm)} km
                  </span>
                </div>
              </div>

              {/* Wave 14ca-fix64: CB / Rain / Pocket-Mode-Toggles wurden
                  aus dieser Admin-Section heraus in die neue
                  "Display & accessibility"-Section verschoben (oben),
                  sichtbar fuer alle User. User-Wunsch: "drei Features
                  sollten fuer jeden User da sein, nicht nur fuer den
                  Admin". Discovery-Mode bleibt admin-gated (das ist eine
                  Routing-Modification, kein UX-Setting). */}
            </div>
          </div>
        </section>
      )}

      {/* Wave 14ca-fix7 — Notifications-Toggle. Sichtbar auch nach dem
          ersten "Not now" oder "Enable" auf der Permission-Karte; hier
          kann der User seine Entscheidung jederzeit ändern. */}
      <NotificationsSection />

      {/* Wave 14bz — Account-Section mit Logout. Wichtig für den
          Hard-Cap-Device-Flow: wenn der User auf einem Gerät ausloggen
          will (z.B. um Platz für ein neues zu machen), braucht er einen
          klar sichtbaren Logout-Button. */}
      <AccountSection />

      {/* Wave 14ca-fix27 — Legal-Links. Pflichtangaben (Impressum,
          Datenschutz) müssen aus jeder authentifizierten Page erreichbar
          sein. Wir parken sie unten in Profile damit sie nicht als
          Hauptaktion wirken aber jederzeit auffindbar sind. */}
      <LegalLinks />
    </div>
  );
}

/**
 * Wave 14ca-fix27: zwei Text-Links zu /legal?tab=imprint und
 * /legal?tab=privacy. Bewusst dezent (kleiner Text, weiß/55%) damit sie
 * nicht von Notifications/Logout ablenken — aber sichtbar genug dass der
 * User sie findet wenn er sie sucht.
 */
function LegalLinks() {
  return (
    <section className="mt-6 pt-4 border-t border-turbo-700/40">
      <div className="flex gap-4 justify-center text-xs text-white/55">
        <a href="/legal?tab=imprint" className="text-white/55 no-underline">
          Imprint
        </a>
        <span aria-hidden="true">·</span>
        <a href="/legal?tab=privacy" className="text-white/55 no-underline">
          Privacy
        </a>
      </div>
    </section>
  );
}

/**
 * Wave 14ca-fix7: Notifications-Section.
 *
 * Zeigt den aktuellen Push-Subscription-Zustand und einen Toggle. Polled
 * den Browser-State live (Notification.permission + pushManager-
 * Subscription) damit der User nach einer manuellen Browser-Settings-
 * Änderung den korrekten Zustand sieht.
 *
 * UI-Zustände:
 *   - Browser unsupported       → Hinweis-Block, keine Toggle.
 *   - Permission == 'denied'    → Hinweis "in Browser-Settings reverten",
 *                                 Toggle disabled (Browser blockiert
 *                                 weitere requestPermission()-Calls).
 *   - subscribed == true        → Grüner Toggle ON, Klick = unsubscribe.
 *   - subscribed == false       → Toggle OFF, Klick = subscribe-Flow
 *                                 (kann den Permission-Prompt öffnen).
 */
function NotificationsSection() {
  const [state, setState] = useState<PushSubscribedState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!isPushSupported()) {
      setState({ supported: false, permission: 'unsupported', subscribed: false });
      return;
    }
    try {
      setState(await getPushState());
    } catch {
      /* SW nicht ready — versuchen wir's später nochmal */
    }
  };

  useEffect(() => {
    void refresh();
    // Wenn der User in Browser-Settings die Permission ändert, kommt KEIN
    // Event — wir refreshen daher beim Tab-Focus, das ist die häufigste
    // "Was hat sich geändert"-Situation.
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const handleToggle = async () => {
    if (busy || !state) return;
    setBusy(true);
    setError(null);
    try {
      if (state.subscribed) {
        await pushUnsubscribe();
        // localStorage so setzen, dass die PermissionCard nicht direkt
        // wieder erscheint — der User hat eben EXPLIZIT abgewählt.
        try {
          window.localStorage.setItem('adt_push_prompt_state', 'declined');
        } catch {
          /* no-op */
        }
      } else {
        await pushSubscribe();
        try {
          window.localStorage.setItem('adt_push_prompt_state', 'enabled');
        } catch {
          /* no-op */
        }
      }
      await refresh();
    } catch (err) {
      const code = err instanceof Error ? err.message : 'unknown';
      setError(
        code === 'permission_denied'
          ? 'Browser blocked notifications. Allow them in your browser settings, then come back.'
          : code === 'no_public_key'
            ? 'Notifications are not configured on the server yet.'
            : "Couldn't update notification settings. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (state === null) {
    return null; // Mount-Tick — vermeidet Flicker.
  }

  return (
    <section className="space-y-3 rounded-2xl border border-turbo-700/50 bg-turbo-900/30 p-4 mt-4">
      <h2 className="text-sm font-semibold text-white/85 uppercase tracking-wide">
        Notifications
      </h2>
      {!state.supported ? (
        <p className="text-xs text-white/60 leading-relaxed">
          Your browser doesn't support push notifications. On iPhone, install
          this app to your home screen and open it from there to enable them.
        </p>
      ) : state.permission === 'denied' ? (
        <div className="space-y-2">
          <p className="text-xs text-amber-200/85 leading-relaxed">
            Notifications are blocked by your browser. Allow them in browser
            settings → site settings → Notifications, then tap “I enabled it”
            below.
          </p>
          {/* Wave 14ca-fix64: Retry-Button. iOS Safari kann nach manueller
              Permission-Aenderung im Settings-Panel den 'default'-State
              zurueckliefern; ein expliziter Tap schubst die UI in den
              richtigen Zustand statt darauf zu warten dass der User
              Reload macht. */}
          <button
            type="button"
            onClick={async () => {
              await refresh();
              const fresh = await getPushState();
              if (fresh.permission !== 'denied') {
                // Direkt durchsubscriben — User hat klar zugestimmt.
                void handleToggle();
              }
            }}
            className="w-full text-xs px-3 py-2 rounded-lg bg-turbo-700/60 hover:bg-turbo-700 text-white/85 border border-white/15"
          >
            I enabled it — try again
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleToggle}
            disabled={busy}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition-colors ${
              state.subscribed
                ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-100'
                : 'bg-turbo-800/40 border-turbo-700/50 text-white/85 hover:bg-turbo-800/60'
            } disabled:opacity-50`}
            aria-pressed={state.subscribed}
          >
            <span className="text-sm font-medium">
              {state.subscribed ? 'Notifications enabled' : 'Enable notifications'}
            </span>
            <span
              className={`relative inline-flex w-10 h-6 rounded-full transition-colors ${
                state.subscribed ? 'bg-emerald-500' : 'bg-turbo-700/70'
              }`}
              aria-hidden="true"
            >
              <span
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  state.subscribed ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>
          <p className="text-[11px] text-white/55 leading-snug">
            We'll only ping you when new trails, runs or tours are added. Tap
            a notification to open the new content.
          </p>
        </div>
      )}
      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
    </section>
  );
}

/**
 * Account-Section: zeigt den eingeloggten User + Logout-Button.
 * Beim Logout: POST /api/auth/logout → Server revoked die Session,
 * danach löschen wir den localStorage-Token und reloaden die Seite
 * (AuthGate zeigt dann den Login-Screen).
 */
function AccountSection() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMe().then((u) => {
      if (!cancelled) setUser(u);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onLogout = async () => {
    if (loggingOut) return;
    if (!window.confirm('Sign out on this device?')) return;
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      // Wave 14bz-fix: statt simplem reload gehen wir auf die Root-URL mit
      // einem Cache-Bust-Param. Verhindert, dass ein Service-Worker die
      // alte "signed-in" App-Shell aus dem Cache liefert und der User
      // weiterhin eingeloggt aussieht. Der Bust-Param ist ein Timestamp,
      // so dass JEDE Logout-Navigation als frische URL zählt.
      window.location.href = `/?_logout=${Date.now()}`;
    }
  };

  if (!user) {
    return (
      <section>
        <h2 className="text-lg font-semibold mb-2">Account</h2>
        <p className="text-sm text-white/55">Not signed in.</p>
      </section>
    );
  }

  return (
    <section>
      <h2 className="text-lg font-semibold mb-2">Account</h2>
      <div className="rounded-lg bg-turbo-700/30 border border-turbo-700 p-3 space-y-2">
        <div className="text-sm">
          <div className="text-white">
            {user.firstName} {user.lastName}
          </div>
          <div className="text-white/55 text-xs break-all">{user.email}</div>
          <div className="text-white/45 text-[11px] mt-0.5">
            Role: <span className="font-mono">{user.role}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          disabled={loggingOut}
          className="w-full py-2 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 active:bg-rose-500/40 border border-rose-400/30 text-rose-200 text-sm font-semibold disabled:opacity-50"
        >
          {loggingOut ? 'Signing out…' : 'Sign out on this device'}
        </button>
        <p className="text-[11px] text-white/45 leading-snug">
          You'll be signed out here but your other devices stay active. To
          free up a slot for another device, sign out here first — then
          tap the magic-link on the new device.
        </p>
      </div>
    </section>
  );
}

/**
 * Zeigt die Offline-Queue für Admin-Crux-Edits (cruxQueue in IDB):
 * - Kein pending-Item → gar nicht sichtbar (keine Karteileichen-Zeile).
 * - 1+ items → Badge mit Anzahl, letzter Fehlermeldung, manueller »Sync now«.
 *
 * Refresht automatisch wenn:
 *   - das Component mountet,
 *   - die Queue sich ändert (CRUX_QUEUE_CHANGED_EVENT wird von admin.ts emittet),
 *   - online-Status wechselt (damit der User den "back online"-Flush sieht),
 *   - der User den Admin-Token ändert (neuer Token könnte alte Items freigeben).
 */
function PendingCruxSync({ adminToken }: { adminToken: string }) {
  const online = useOnline();
  const [items, setItems] = useState<PendingCruxItem[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const list = await listPendingCruxItems();
      if (!cancelled) setItems(list);
    };
    refresh();
    window.addEventListener(CRUX_QUEUE_CHANGED_EVENT, refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(CRUX_QUEUE_CHANGED_EVENT, refresh);
    };
  }, [adminToken, online]);

  if (items.length === 0) return null;

  const onSyncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    setLastResult(null);
    try {
      const res = await flushCruxQueue(adminToken);
      if (res.synced > 0 && res.stillPending === 0) {
        setLastResult(`Synced ${res.synced} — all done.`);
      } else if (res.synced > 0) {
        setLastResult(`Synced ${res.synced}, ${res.stillPending} still pending.`);
      } else {
        setLastResult(`Tried ${res.tried}, none synced — see error below.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setLastResult(`Sync crashed: ${msg}`);
    } finally {
      setSyncing(false);
    }
  };

  const totalCruxes = items.reduce((sum, it) => sum + it.count, 0);
  // Letzter bekannter Fehler (egal aus welchem Item) — der hilft fürs Debugging.
  const lastError = items.find((it) => it.lastError)?.lastError;
  const hasAuthIssue = lastError?.includes('admin_token');

  return (
    <div className="mt-3 -mx-1 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs leading-snug">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-amber-300 font-semibold">⏳ Pending crux sync</span>
        <span className="px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-100 font-semibold">
          {items.length} trail{items.length === 1 ? '' : 's'} · {totalCruxes} crux{totalCruxes === 1 ? '' : 'es'}
        </span>
      </div>
      <div className="text-white/70">
        {hasAuthIssue
          ? 'Token looks wrong — paste a valid ADMIN_TOKEN above, then sync.'
          : online
          ? 'These crux edits haven\'t reached the server yet. Tap »Sync now« to retry.'
          : 'You are offline. These will push automatically when you reconnect.'}
      </div>
      {lastError && !hasAuthIssue && (
        <div className="text-white/50 mt-1 break-words">Last error: {lastError}</div>
      )}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={onSyncNow}
          disabled={syncing || !online || !adminToken}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            syncing || !online || !adminToken
              ? 'bg-white/10 text-white/40 cursor-not-allowed'
              : 'bg-amber-500 hover:bg-amber-400 text-black'
          }`}
        >
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
        {!online && <span className="text-white/50">(offline)</span>}
        {!adminToken && <span className="text-white/50">(no token)</span>}
        {lastResult && <span className="text-white/70">{lastResult}</span>}
      </div>
    </div>
  );
}

/** Slider + Wertanzeige für Zoomstufen. Ganzzahlige Schritte. */
function ZoomSlider({
  label,
  hint,
  min,
  max,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium">{label}</span>
        <span className="text-sm text-white/70 tabular-nums">Level {Math.round(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-2 accent-turbo-500"
        aria-label={label}
      />
      <div className="flex justify-between text-[10px] text-white/40 -mt-1">
        <span>{min}</span>
        <span>{max}</span>
      </div>
      <div className="text-xs text-white/60 mt-1">{hint}</div>
    </label>
  );
}

/** Kleine Pill die live anzeigt ob der Wake Lock tatsächlich greift. */
function WakeLockBadge({
  active,
  supported,
  pending,
}: {
  active: boolean;
  supported: boolean;
  pending: boolean;
}) {
  if (!supported) {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60">
        unavailable
      </span>
    );
  }
  if (active) {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
        ● active
      </span>
    );
  }
  if (pending) {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
        ○ waiting
      </span>
    );
  }
  return null;
}
